import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { adminCommandSchema } from "./schemas";

// Every type this client writes to events/{eventId}/admin_commands, matching
// the types firestore.rules admits there. IMPORT_ATTENDEES is written directly
// by ImportSection (its id has to exist before the upload), never through
// submit(), and is listed so the feedback wording covers it too.
export const ADMIN_COMMAND_TYPES = [
  "ADD_EVENT_ATTENDEE",
  "ASSIGN_ATTENDEE_TRACKS",
  "CANCEL_SCHEDULED_ANNOUNCEMENT",
  "DECIDE_TRACK_SWITCH",
  "DECIDE_SEAT_REQUEST",
  "DELETE_EVENT_ANNOUNCEMENT",
  "DELETE_FEEDBACK_FORM",
  "DELETE_RESOURCE",
  "DELETE_SEATING",
  "DELETE_SPEAKER",
  "DELETE_TRACK",
  "DELETE_VENUE",
  "DISMISS_IMPORT_ISSUES",
  "IMPORT_ATTENDEES",
  "MINT_INVITE",
  "PUBLISH_SEATING",
  "RESCHEDULE_AGENDA",
  "RESOLVE_IMPORT_ISSUE",
  "RESTORE_EVENT_ATTENDEE",
  "REVOKE_INVITE",
  "SAVE_AFTER_EVENT_SUMMARY",
  "SCHEDULE_EVENT_ANNOUNCEMENT",
  "SAVE_FEEDBACK_FORM",
  "SEND_EVENT_ANNOUNCEMENT",
  "SET_EVENT_SLUG",
  "SUGGEST_SEATING",
  "UNPUBLISH_SEATING",
  "UPDATE_EVENT_DESCRIPTION",
  "UPDATE_SCHEDULED_ANNOUNCEMENT",
] as const;
export type AdminCommandType = (typeof ADMIN_COMMAND_TYPES)[number];

// `type` rides every state after a submit so the feedback strip can say what
// the command did rather than a generic "Saved".
export type AdminCommandState =
  | { status: "idle" }
  | { status: "submitting"; type: AdminCommandType }
  | { status: "watching"; type: AdminCommandType; commandStatus: string }
  // `invite` carries the MINT_INVITE outcome — the raw token rides the
  // command document, because the minters are who may read this collection
  // and the token is the thing only the minter ever receives.
  | {
      status: "succeeded";
      type: AdminCommandType;
      invite?: { token: string };
      // Settled as a duplicate of a run already in progress.
      deferred?: boolean;
      // SUGGEST_SEATING: seats left as they were, changed meanwhile.
      keptSeats?: number;
    }
  | { status: "failed"; error: string };

// Writes to events/{eventId}/admin_commands per the reference contract.
// Backend dependency: isEventAdmin rule + admin command trigger.
export function useAdminCommand() {
  const [state, setState] = useState<AdminCommandState>({ status: "idle" });
  const unsubRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      unsubRef.current?.();
    };
  }, []);

  const submit = useCallback(
    async (
      type: AdminCommandType,
      eventId: string,
      uid: string,
      payload?: Record<string, unknown>,
    ) => {
      unsubRef.current?.();
      unsubRef.current = null;
      setState({ status: "submitting", type });

      try {
        const data: Record<string, unknown> = {
          type,
          requestedBy: uid,
          requestedAt: serverTimestamp(),
          eventId,
          status: "REQUESTED",
        };
        if (payload) data.payload = payload;

        const docRef = await addDoc(
          collection(db, "events", eventId, "admin_commands"),
          data,
        );

        setState({ status: "watching", type, commandStatus: "REQUESTED" });

        const unsub = onSnapshot(
          doc(db, "events", eventId, "admin_commands", docRef.id),
          (snap) => {
            if (!snap.exists()) return;
            const result = adminCommandSchema.safeParse(snap.data());
            if (!result.success) return;

            const cmd = result.data;
            if (cmd.status === "SUCCEEDED") {
              unsub();
              unsubRef.current = null;
              setState({
                status: "succeeded",
                type,
                invite:
                  cmd.type === "MINT_INVITE" && cmd.token
                    ? { token: cmd.token }
                    : undefined,
                deferred: cmd.deferredTo ? true : undefined,
                keptSeats: cmd.keptSeats,
              });
            } else if (cmd.status === "FAILED") {
              unsub();
              unsubRef.current = null;
              setState({
                status: "failed",
                error: cmd.error ?? "Command failed",
              });
            } else {
              setState({ status: "watching", type, commandStatus: cmd.status });
            }
          },
          (err) => {
            unsub();
            unsubRef.current = null;
            setState({
              status: "failed",
              error:
                err instanceof Error ? err.message : "Failed to watch command",
            });
          },
        );

        unsubRef.current = unsub;
      } catch (err) {
        setState({
          status: "failed",
          error:
            err instanceof Error ? err.message : "Failed to submit command",
        });
      }
    },
    [],
  );

  const reset = useCallback(() => {
    unsubRef.current?.();
    unsubRef.current = null;
    setState({ status: "idle" });
  }, []);

  return { state, submit, reset };
}
